import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../clients/apiClient';
import { showGlobalToast } from '../utils/toast';
import { useAuth } from './useAuth';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

async function fetchNotifications(): Promise<Notification[]> {
  const { data } = await apiClient.get<Notification[]>('/api/notifications');
  return data;
}

export async function markRead(id: string): Promise<void> {
  await apiClient.patch(`/api/notifications/${id}/read`);
}

export async function markAllRead(): Promise<void> {
  await apiClient.patch('/api/notifications/read-all');
}

// Module-level set persists across page navigation & component remounts in the active tab session
const sessionSeenIds = new Set<string>();
let isInitialNotificationsFetch = true;

/** Format batch notifications into strictly ONE clean, unified toast */
function formatBatchToast(notifications: Notification[]): { message: string; type: 'success' | 'info' } {
  if (notifications.length === 1) {
    const n = notifications[0];
    const isSuccess = n.type === 'drafts_ready' || n.type === 'bounce_refund';
    return { message: n.title, type: isSuccess ? 'success' : 'info' };
  }

  const drafts = notifications.filter(n => n.type === 'drafts_ready');
  const bounces = notifications.filter(n => n.type === 'bounce_refund');
  const followUps = notifications.filter(n => n.type.startsWith('follow_up'));
  const others = notifications.filter(n => n.type !== 'drafts_ready' && n.type !== 'bounce_refund' && !n.type.startsWith('follow_up'));

  // All drafts
  if (drafts.length === notifications.length) {
    const firstTitle = drafts[0].title;
    const firstCompany = firstTitle.replace(/^Drafts ready for\s*/i, '').trim();
    const othersCount = drafts.length - 1;
    const label = othersCount === 1 ? '1 other' : `${othersCount} others`;
    return {
      message: `Drafts ready for ${firstCompany} + ${label}`,
      type: 'success',
    };
  }

  // All bounces
  if (bounces.length === notifications.length) {
    let totalRefunded = 0;
    bounces.forEach(n => {
      const match = n.title.match(/^(\d+)\s+credit/i);
      totalRefunded += match ? parseInt(match[1], 10) : 1;
    });
    const count = totalRefunded || bounces.length;
    return {
      message: `${count} credits refunded: emails bounced`,
      type: 'success',
    };
  }

  // All follow-ups
  if (followUps.length === notifications.length) {
    return {
      message: `${followUps.length} follow-up updates`,
      type: 'info',
    };
  }

  // All others
  if (others.length === notifications.length) {
    return {
      message: `${others.length} updates in your notifications`,
      type: 'info',
    };
  }

  // Mixed batch arriving in the same tick: unified compact summary
  const parts: string[] = [];
  if (drafts.length > 0) parts.push(`${drafts.length} draft${drafts.length > 1 ? 's' : ''} ready`);
  if (bounces.length > 0) parts.push(`${bounces.length} credit${bounces.length > 1 ? 's' : ''} refunded`);
  if (followUps.length > 0) parts.push(`${followUps.length} follow-up${followUps.length > 1 ? 's' : ''}`);
  if (others.length > 0) parts.push(`${others.length} other${others.length > 1 ? 's' : ''}`);

  return {
    message: parts.join(' · '),
    type: drafts.length > 0 || bounces.length > 0 ? 'success' : 'info',
  };
}

export function useNotifications() {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const query = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 30_000,
  });

  // Show toasts only for genuinely new unread notifications arriving during active session
  useEffect(() => {
    const notifications = query.data ?? [];
    if (notifications.length === 0) return;

    // On initial session load, seed all current notification IDs so existing unread items don't toast
    if (isInitialNotificationsFetch) {
      isInitialNotificationsFetch = false;
      notifications.forEach(n => sessionSeenIds.add(n.id));
      return;
    }

    const newUnseen = notifications.filter(
      n => !n.read && !sessionSeenIds.has(n.id)
    );

    // Record all notifications in sessionSeenIds so we don't re-toast on subsequent refetches
    notifications.forEach(n => sessionSeenIds.add(n.id));

    if (newUnseen.length === 0) return;

    // If new balance-affecting notifications arrived, update user & pipeline immediately ONCE
    const hasBalanceAffecting = newUnseen.some(n => n.type === 'drafts_ready' || n.type === 'bounce_refund');
    if (hasBalanceAffecting) {
      refreshUserRef.current();
      queryClientRef.current.invalidateQueries({ queryKey: ['pipeline'] });
    }

    // Unify all new incoming notifications into strictly ONE clean toast
    const batchToast = formatBatchToast(newUnseen);
    showGlobalToast(batchToast.message, batchToast.type);
  }, [query.data]);



  const unreadCount = (query.data ?? []).filter(n => !n.read).length;

  const handleMarkAllRead = async () => {
    await markAllRead();
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const handleMarkRead = async (id: string) => {
    await markRead(id);
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  return {
    notifications: query.data ?? [],
    unreadCount,
    markRead: handleMarkRead,
    markAllRead: handleMarkAllRead,
  };
}
